import { spErrorTags } from "@eventr/common";
import localForage from "localforage";
import { debounce, type DebouncedFunc } from "lodash";
import type { PersistStorage, StorageValue } from "zustand/middleware";
import { useStorageStore } from "./storage/storage.store";

// A dedicated instance, not localForage.config(): config() is silently ignored once any
// other module has touched the default instance, which sent cold deep-link boots on iOS
// to the wrong database (dc1a61332). Nothing else may open "keyval-store" directly — on
// an empty database localForage has to upgrade it to create "keyval", and a connection
// left open elsewhere blocks that upgrade until getItem's 20s timeout below gives up.
const localForageToUse = localForage.createInstance({
  name: "keyval-store",
  storeName: "keyval",
});

// The latest value per store, serialised only when its debounced write runs.
const pendingValues = new Map<string, StorageValue<unknown>>();

const writePending = (name: string): Promise<void> => {
  const value = pendingValues.get(name);
  if (!value) return Promise.resolve();
  pendingValues.delete(name);
  return localForageToUse.setItem(name, JSON.stringify(value)).then(() => undefined);
};

// One debounce per store. The debounced body returns the write's promise, so flush() resolves
// when the write lands.
const debouncedWrites = new Map<string, DebouncedFunc<() => Promise<void>>>();

const debouncedWrite = (name: string) => {
  let write = debouncedWrites.get(name);
  if (!write) {
    write = debounce(() => writePending(name), 1000);
    debouncedWrites.set(name, write);
  }
  return write;
};

const flushAllWrites = () => Promise.all([...debouncedWrites.values()].map((write) => write.flush()));

// A fresh native webview for a gated tab (Team, Chat) can boot before the debounce above flushes,
// reading a stale/absent IDtoken and bouncing to sign-in. Flush immediately on IDtoken change only.
const lastFlushedIDtoken = new Map<string, unknown>();

let inFlightWrites = 0;
let flushGate: Promise<void> = Promise.resolve();
let settleGate: (() => void) | null = null;

/** Flushes pending persisted-store writes. */
export function flushStoredWrites(): Promise<void> {
  if (inFlightWrites === 0) {
    settleGate = null;
    flushGate = new Promise<void>((resolve) => {
      settleGate = resolve;
    });
  }
  const write = flushAllWrites().catch((error) => {
    spErrorTags("flushStoredWrites failed", ["STORAGE"], error);
  });
  inFlightWrites += 1;
  void write.finally(() => {
    inFlightWrites -= 1;
    if (inFlightWrites === 0) {
      settleGate?.();
      settleGate = null;
    }
  });
  return flushGate;
}

const READ_TIMEOUT_MS = 20000;
const READ_TIMED_OUT = Symbol("read timed out");

// Stores whose startup read timed out. Nothing is written to one until its state carries an IDtoken.
const unreadStores = new Set<string>();

export const indexDbStorage = {
  getItem: async (name: string): Promise<string | null> => {
    console.log("Zustand getItem for", name);

    let timeout: ReturnType<typeof setTimeout> | undefined;
    const value = await Promise.race([
      localForageToUse.getItem<string>(name, (error) => {
        console.log("Zustand getItem callback", error);
      }),
      new Promise<typeof READ_TIMED_OUT>((resolve) => {
        timeout = setTimeout(() => resolve(READ_TIMED_OUT), READ_TIMEOUT_MS);
      }),
    ]).finally(() => clearTimeout(timeout));

    // An open queued behind a pending delete of the database never settles.
    if (value === READ_TIMED_OUT) {
      spErrorTags(`Zustand getItem for ${name} timed out`, ["STORAGE"]);
      unreadStores.add(name);
      return null;
    }

    if (value) {
      console.log("Zustand getItem value");
    } else {
      console.log("Zustand getItem value not found");
    }

    return value || null;
  },
  removeItem: async (name: string): Promise<void> => {
    console.log("Zustand removeItem for", name);
    await localForageToUse.removeItem(name);
  },
};

/** Persist storage for the zustand stores. A value stays an object until its debounced write serialises it. */
export const indexDbPersistStorage = <S>(): PersistStorage<S> => ({
  getItem: async (name) => {
    const raw = await indexDbStorage.getItem(name);
    return raw === null ? null : (JSON.parse(raw) as StorageValue<S>);
  },
  setItem: (name, value) => {
    if (useStorageStore.getState().hasHydrated !== "finished") {
      console.log(`Zustand setItem ${name} fail as not yet hydrated`);
      return;
    }

    const IDtoken = (value.state as { IDtoken?: unknown } | undefined)?.IDtoken;
    if (unreadStores.has(name)) {
      if (!IDtoken) return;
      unreadStores.delete(name);
    }

    pendingValues.set(name, value);
    const write = debouncedWrite(name);
    write();

    if (IDtoken !== lastFlushedIDtoken.get(name)) {
      lastFlushedIDtoken.set(name, IDtoken);
      void write.flush();
    }
  },
  removeItem: (name) => indexDbStorage.removeItem(name),
});
